import { type ClassValue, clsx } from 'clsx';

import forge from 'node-forge';
import CryptoJS from 'crypto-js';
import { JSEncrypt } from 'jsencrypt';
import moment from "moment";

import { CalendarDays, FileQuestion, IdCard, ListTodo, School, ShieldQuestion } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import {
  CreateSlotPayload,
  ExamSlotDetails,
  FormErrors,
  StepExamInterface,
  TimeConfig,
} from "./interfaces/exam.interface";
import { EXAM_MODE_ENUM, MonitoringDashboardExamStatus } from './environment';

export function isInViewport(element: any) {
  const rect = element?.getBoundingClientRect();
  return (
    rect &&
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <=
      (window.innerWidth + 50 || document.documentElement.clientWidth + 50)
  );
}

export function generateAlphabet(capital = false) {
  return [...Array(26)].map((_, i) =>
    String.fromCharCode(i + (capital ? 65 : 97))
  );
}

export function TweleveHourFormat(values: string) {
  const filteredTime = values.includes("T")
    ? values.split("T")?.[1] ?? values
    : values;
  let hours: any = filteredTime.split(":")?.[0]; // gives the value in 24 hours format
  const AmOrPm = Number(hours) >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  const minutes = values.split(":")?.[1]; // gives the value in 24 hours format
  return `${hours + ":" + minutes + " " + AmOrPm}`;
}

export const Utilfilesizes = (bytes: any, decimals: number = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const examSteps = [
  {
    stepNumber: 1,
    Icon: FileQuestion,
    title: "Create new Exam and Paper(s)",
    details:
      "Set up a new exam by specifying the exam name, organizer, number of papers, instructions, paper name, exam duration, total marks, and the number of questions.",
  },
  {
    stepNumber: 2,
    Icon: ShieldQuestion,
    title: "Allocate Questions and Answer Keys for each Paper",
    details:
      "Distribute questions across each exam paper based on the defined syllabus or requirement.",
  },
  {
    stepNumber: 3,
    title: "Question Set Randomization",
    Icon: ListTodo,
    details:
      "Organize questions into sections, and assign marks, subjects, and complexity levels to create various question combinations.",
  },
  {
    stepNumber: 4,
    title: "Manage Exam Slot",
    Icon: CalendarDays,
    details:
      "Define multiple slots for each exam paper by assigning the exam date, start and end time, and the number of question sets per slot.",
  },
  {
    stepNumber: 5,
    title: "Allot Candidate to Exam Center & Slot",
    Icon: School,
    details:
      "Allocate exam centers, assign examinees to each center, and distribute question sets accordingly.",
  },
  {
    stepNumber: 6,
    title: "Allot & Lock Question Set",
    Icon: School,
    details:
      "Allocate exam centers, assign examinees to each center, and distribute question sets accordingly.",
  },
];


export function to12HourFormat(time24: string): string {
  const [hourStr, minuteStr] = time24.split(":");
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
}

export function addMinutesToTime(timeStr: string, minutesToAdd: number) {
  const [hours, minutes, seconds] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);
  date.setMinutes(date.getMinutes() + minutesToAdd);
  return date.toTimeString().split(" ")[0];
}

export function isFirstDateNotPast(firstDateStr: any, secondDateStr: any) {
  const firstDate = new Date(firstDateStr);
  const secondDate = new Date(secondDateStr);

  return firstDate.getTime() >= secondDate.getTime();
}

export const regexMapper = {
  GSTRegex:
    /^([0][1-9]|[1-2][0-9]|[3][0-7])([a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[1-9a-zA-Z]{1}[zZ]{1}[0-9a-zA-Z]{1})+$/,
  IFSCCodeRegex: /^[A-Za-z]{4}0[A-Z0-9]{6}$/,
  mobileNumberRegex: /^(\+\d{1,3}[- ]?)?\d{10}$/,
  PANRegex: /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/,
  EmailRegex:
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  checkPANIsValid(value: any): boolean {
    return this.PANRegex.test(value);
  },
  checkIFSCCodeValid(value: any) {
    return this.IFSCCodeRegex.test(value);
  },
  checkEmailIsValid(value: any) {
    return this.EmailRegex.test(value);
  },
  checkGSTIsValid(value: any): boolean {
    return this.GSTRegex.test(value);
  },
  checkMobileNumber(value: any): boolean {
    return this.mobileNumberRegex.test(value);
  },
};

export function downloadBase64PDF(base64Data: any, filename = "file.pdf") {
  const decoded = decodeURIComponent(escape(atob(base64Data)));
  const linkSource = `data:application/pdf;base64,${decoded}`;
  const downloadLink = document.createElement("a");

  downloadLink.href = linkSource;
  downloadLink.download = filename;
  downloadLink.click();
}

export function isSlotConflicting({
  newSlot,
  existingSlots,
}: {
  newSlot: CreateSlotPayload;
  existingSlots: Array<ExamSlotDetails>;
}) {
  const newStart = new Date(`${newSlot.exam_date}T${newSlot.start_time}`);
  const newEnd = new Date(`${newSlot.exam_date}T${newSlot.end_time}`);

  return existingSlots.some((slot) => {
    if (slot.exam_date !== newSlot.exam_date) return false;

    const existingStart = new Date(`${slot.exam_date}T${slot.start_time}`);
    const existingEnd = new Date(`${slot.exam_date}T${slot.end_time}`);

    // Check for overlap
    return newStart < existingEnd && newEnd > existingStart;
  });
}



 // adjust the import paths as needed

export const getExamStatus = ({examMode,slotDetails,centerDetails}:{examMode:any, slotDetails:any,centerDetails:any}) => {
  // Validate inputs
  if (examMode !== EXAM_MODE_ENUM.ONLINE || !slotDetails) {
    return centerDetails; // fallback/default
  }

  // Check if exam date is today
  const isToday =
    moment(slotDetails.exam_date).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
    
    
    

  // Parse start and end times
  const now = new Date().getTime();
  const startTime = new Date(`${slotDetails.exam_date} ${slotDetails.start_time}`).getTime();
  const endTime = new Date(`${slotDetails.exam_date} ${slotDetails.end_time}`).getTime();
  
  

  // 1. Result Published
  if (slotDetails.status === "PUBLISHED") {
    return MonitoringDashboardExamStatus.RESULT_PUBLISHED;
  }

  // 2. Only process status if exam is scheduled for today
  if (isToday) {
    // 2.a. Exam Not Started
    if (now < startTime) {

      return MonitoringDashboardExamStatus.EXAM_NOT_STARTED;
    }

    // 2.b. Exam In Progress
    if (now >= startTime && now <= endTime) {

      return MonitoringDashboardExamStatus.EXAM_IN_PROGRESS;
    }

    // 2.c. Exam Completed
    if (now > endTime) {

      return MonitoringDashboardExamStatus.EXAM_COMPLETED;
    }
  }


  // Default status
  return centerDetails;
};






const PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQDHZfOr1pWpI+lZNZJXtvUG5TV1
qRTQimh495h3q33ycGyCG1Aor+Qlug05Bm8jhAzbTlX3iuk16119W9F71KoJjo+D
mNW35UeKE72109UfGqcFWp7kvOKGCUihQBvK2teML2dO48+D7RD805eL9i3SDu+6
0Ckn1oKDff2He/PbUQIDAQAB
-----END PUBLIC KEY-----`;

export function encryptWithFixedKey(data:any) {
  
  data = typeof data === "string" ? data : JSON.stringify(data);
  const aesKey = CryptoJS.lib.WordArray.random(32); // 256-bit
  const iv = CryptoJS.lib.WordArray.random(16); // 128-bit

  const encryptedData = CryptoJS.AES.encrypt(data, aesKey, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();

  const aesKeyBase64 = CryptoJS.enc.Base64.stringify(aesKey);
  const ivBase64 = CryptoJS.enc.Base64.stringify(iv);

  const encryptor = new JSEncrypt();
  encryptor.setPublicKey(PUBLIC_KEY);

  const encryptedKey = encryptor.encrypt(aesKeyBase64);
  const encryptedIv = encryptor.encrypt(ivBase64);


  return {
    encrypted_data:encryptedData,
    encrypted_key:encryptedKey,
    encrypted_iv:encryptedIv,
  };
}


export function generateRandomBytes() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  [...new Array(32)].forEach((self)=>{
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  })
  
  return result;
}

export function decryptWithFixedKey(encryptedData: {
  encrypted_data: string;
  encrypted_key: string;
  encrypted_iv: string;
}, privateKey: string): any {
  // Decrypt the AES key and IV using RSA private key
  const decryptor = new JSEncrypt();
  decryptor.setPrivateKey(privateKey);

  const aesKeyBase64 = decryptor.decrypt(encryptedData.encrypted_key);
  const ivBase64 = decryptor.decrypt(encryptedData.encrypted_iv);

  if (!aesKeyBase64 || !ivBase64) {
    throw new Error('Failed to decrypt AES key or IV');
  }

  // Convert Base64 strings back to WordArrays
  const aesKey = CryptoJS.enc.Base64.parse(aesKeyBase64);
  const iv = CryptoJS.enc.Base64.parse(ivBase64);

  // Decrypt the data
  const decrypted = CryptoJS.AES.decrypt(
    encryptedData.encrypted_data,
    aesKey,
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    }
  );

  // Convert to UTF-8 string
  const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

  // Try to parse as JSON, return as string if not parseable
  try {
    return JSON.parse(decryptedStr);
  } catch (e) {
    return decryptedStr;
  }
}



// export async function decryptAES256CBC(base64Ciphertext:string, base64Key:string):Promise<any> {
//   // Convert Base64 ciphertext and key to byte arrays
//   const ciphertextBytes = Uint8Array.from(atob(base64Ciphertext), c => c.charCodeAt(0));
//   const keyBytes = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));

//   // Extract IV (first 16 bytes) and actual ciphertext
//   const iv = ciphertextBytes.slice(0, 16);
//   const actualCiphertext = ciphertextBytes.slice(16);

//   // Import key
//   const cryptoKey = await crypto.subtle.importKey(
//     "raw",
//     keyBytes,
//     { name: "AES-CBC" },
//     false,
//     ["decrypt"]
//   );

//   // Decrypt
//   const decryptedBuffer = await crypto.subtle.decrypt(
//     {
//       name: "AES-CBC",
//       iv: iv
//     },
//     cryptoKey,
//     actualCiphertext
//   );

//   const decoder = new TextDecoder();
//   return  decoder.decode(decryptedBuffer)
// }


export function isJson(str:any) {
  try {
    const parsed = JSON.parse(JSON.stringify(str));
    return typeof parsed === 'object' && parsed !== null;
  } catch (e) {
    return false;
  }
}




export async function decryptAES256CBC2(
  base64Ciphertext: string,
  base64Key: string
): Promise<string> {
  // Decode Base64 inputs
  const ciphertextBytes = forge.util.decode64(base64Ciphertext);
  const keyBytes = forge.util.decode64(base64Key);

  // Ensure key is 32 bytes (AES-256 requirement)
  if (keyBytes.length !== 32) {
    throw new Error("Key must be 32 bytes (256 bits) for AES-256");
  }

  // Extract IV (first 16 bytes) and actual ciphertext
  const iv = ciphertextBytes.substring(0, 16);
  const encryptedData = ciphertextBytes.substring(16);

  // Create decipher (explicitly using AES-256-CBC)
  const decipher = forge.cipher.createDecipher(
    'AES-CBC', // Still uses 'AES-CBC', but key size determines AES-128/192/256
    forge.util.createBuffer(keyBytes)
  );

  // Configure decipher
  decipher.start({ iv: forge.util.createBuffer(iv) });
  decipher.update(forge.util.createBuffer(encryptedData));
  decipher.finish();

  // Return decrypted UTF-8 string
  return decipher.output.toString();
}




export const validateTimeConfig = (config: TimeConfig, examStartTime: string): FormErrors => {
  const errors: FormErrors = {};

  if (!config.userInfoDownloadTime) {
    errors.userInfoDownloadTime = 'User info download time is required';
  }

  if (!config.examInfoGetTime) {
    errors.examInfoGetTime = 'Exam info get time is required';
  }

  if (!config.questionDownloadTime) {
    errors.questionDownloadTime = 'Question download time is required';
  }

  if (!config.questionUnlockTime) {
    errors.questionUnlockTime = 'Question unlock time is required';
  }

  // Validate that all times are before exam start time
  const examStartMinutes = timeToMinutes(examStartTime);
  
  if (config.userInfoDownloadTime && timeToMinutes(config.userInfoDownloadTime) >= examStartMinutes) {
    errors.userInfoDownloadTime = 'Must be before exam start time';
  }

  if (config.examInfoGetTime && timeToMinutes(config.examInfoGetTime) >= examStartMinutes) {
    errors.examInfoGetTime = 'Must be before exam start time';
  }

  if (config.questionDownloadTime && timeToMinutes(config.questionDownloadTime) >= examStartMinutes) {
    errors.questionDownloadTime = 'Must be before exam start time';
  }

  if (config.questionUnlockTime && timeToMinutes(config.questionUnlockTime) >= examStartMinutes) {
    errors.questionUnlockTime = 'Must be before exam start time';
  }

  // Validate time sequence (each subsequent time should be after the previous)
  if (config.userInfoDownloadTime && config.examInfoGetTime) {
    if (timeToMinutes(config.userInfoDownloadTime) >= timeToMinutes(config.examInfoGetTime)) {
      errors.examInfoGetTime = 'Must be after user info download time';
    }
  }

  if (config.examInfoGetTime && config.questionDownloadTime) {
    if (timeToMinutes(config.examInfoGetTime) >= timeToMinutes(config.questionDownloadTime)) {
      errors.questionDownloadTime = 'Must be after exam info get time';
    }
  }

  if (config.questionDownloadTime && config.questionUnlockTime) {
    if (timeToMinutes(config.questionDownloadTime) >= timeToMinutes(config.questionUnlockTime)) {
      errors.questionUnlockTime = 'Must be after question download time';
    }
  }

  return errors;
};

const timeToMinutes = (timeString: string): number => {
  if (!timeString) return 0;
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

export const formatTime = (timeString: string): string => {
  if (!timeString) return '';
  
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours);
  const minute = parseInt(minutes);
  
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

export const getTimeDifference = (startTime: string, endTime: string): string => {
  if (!startTime || !endTime) return '';
  
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);
  
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  
  const diffMinutes = endTotalMinutes - startTotalMinutes;
  
  if (diffMinutes <= 0) return '';
  
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};


export function base64ToString(base64:any) {
  return decodeURIComponent(
    Array.prototype.map.call(atob(base64), c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join('')
  );
}